'use client';

import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { X, Plus } from 'lucide-react';
import type { SchoolViewExtra } from '@/types/api';
import {
  useUpdateScheduleType,
  useUpdateLunchSlots,
  useCreateLunchClass,
  useDeleteLunchClass,
  useCreateDayType,
  useDeleteDayType,
  useCreatePeriodType,
  useDeletePeriodType,
} from '@/hooks/use-schools';
import { SCHEDULE_TYPES, LUNCH_TYPES } from '@/config/constants';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SetupStep1Props {
  school: SchoolViewExtra;
  schoolId: string;
  onComplete: () => void;
}

export interface Step1Ref {
  save: () => Promise<boolean>;
  canSave: () => boolean;
}

const PERIOD_PRESETS = [
  { label: '7 Periods', value: ['1', '2', '3', '4', '5', '6', '7'] },
  { label: '8 Periods', value: ['1', '2', '3', '4', '5', '6', '7', '8'] },
  { label: '4 Blocks', value: ['1', '2', '3', '4'] },
  { label: '6 Periods', value: ['1', '2', '3', '4', '5', '6'] },
];

// Auto-suggest day types based on schedule type
const getSuggestedDayTypes = (type: string): string[] => {
  switch (type) {
    case 'same_every_day':
    case 'same_every_day_plus':
      return ['REGULAR'];
    case 'a_b':
      return ['A', 'B'];
    case 'a_b_plus':
      return ['A', 'B', 'C'];
    case 'monday_through_friday':
      return ['M', 'T', 'W', 'TH', 'F'];
    case 'multiple_day_rotating':
      return ['1', '2', '3', '4', '5', '6'];
    default:
      return ['A', 'B'];
  }
};

export const SetupStep1ScheduleType = forwardRef<Step1Ref, SetupStep1Props>(
  function SetupStep1ScheduleType({ school, schoolId, onComplete }, ref) {
    const [scheduleType, setScheduleType] = useState(school.scheduleType || '');
    const [customDayTypes, setCustomDayTypes] = useState<string[]>([]);
    const [customPeriodTypes, setCustomPeriodTypes] = useState<string[]>([]);
    const [newDayType, setNewDayType] = useState('');
    const [newPeriodType, setNewPeriodType] = useState('');
    const [lunchType, setLunchType] = useState<'unit' | 'class' | 'waves' | undefined>(
      school.lunchSettings?.type
    );
    const [numLunchWaves, setNumLunchWaves] = useState(
      school.lunchSettings?.type === 'waves' ? school.lunchSettings.slots.length : 3
    );
    
    const hasInitialized = useRef(false);

    const updateScheduleTypeMutation = useUpdateScheduleType(schoolId);
    const updateLunchSlotsMutation = useUpdateLunchSlots(schoolId);
    const createLunchClassMutation = useCreateLunchClass(schoolId);
    const deleteLunchClassMutation = useDeleteLunchClass(schoolId);
    const createDayTypeMutation = useCreateDayType(schoolId);
    const deleteDayTypeMutation = useDeleteDayType(schoolId);
    const createPeriodTypeMutation = useCreatePeriodType(schoolId);
    const deletePeriodTypeMutation = useDeletePeriodType(schoolId);

    const lunchClassId = school.courses?.find((c) => c.name === 'Lunch')?.id;

    // Initialize from existing school data
    useEffect(() => {
      if (hasInitialized.current) return;
      
      // Initialize day types
      if (school.dayTypes && school.dayTypes.length > 0) {
        setCustomDayTypes(school.dayTypes.map(dt => dt.name));
        hasInitialized.current = true;
      } else if (scheduleType) {
        // Auto-suggest based on selected schedule type
        setCustomDayTypes(getSuggestedDayTypes(scheduleType));
        hasInitialized.current = true;
      }

      // Initialize period types
      if (school.periodTypes && school.periodTypes.length > 0) {
        setCustomPeriodTypes(school.periodTypes.map(pt => pt.name));
      } else {
        setCustomPeriodTypes(['1', '2', '3', '4', '5', '6', '7']);
      }
    }, [school, scheduleType]);

    // Check if step has valid data (regardless of save state)
    const hasValidData = 
      scheduleType &&
      customDayTypes.length > 0 &&
      customPeriodTypes.length > 0 &&
      lunchType;

    // Check if step is actually saved to backend
    const isStepSaved = 
      hasValidData &&
      (school.dayTypes?.length || 0) > 0 &&
      (school.periodTypes?.length || 0) > 0;

    useEffect(() => {
      if (isStepSaved) {
        onComplete();
      }
    }, [isStepSaved, onComplete]);

    const schoolPrimaryColor = school.primaryColor || '#1e40af';

    const getContrastColor = (hexColor: string): string => {
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? 'black' : 'white';
    };

    // Handle schedule type selection
    const handleScheduleTypeChange = (type: string) => {
      setScheduleType(type);
      // Auto-suggest day types if none exist
      if (customDayTypes.length === 0 || !school.dayTypes || school.dayTypes.length === 0) {
        setCustomDayTypes(getSuggestedDayTypes(type));
      }
    };

    // Handle period preset selection
    const handlePeriodPresetSelect = (periods: string[]) => {
      setCustomPeriodTypes(periods);
    };

    // Add custom day type
    const addCustomDayType = () => {
      const name = newDayType.trim().toUpperCase();
      if (name && !customDayTypes.includes(name)) {
        setCustomDayTypes([...customDayTypes, name]);
        setNewDayType('');
      }
    };

    // Remove day type
    const removeDayType = (name: string) => {
      setCustomDayTypes(customDayTypes.filter(dt => dt !== name));
    };

    // Add custom period type
    const addCustomPeriodType = () => {
      const name = newPeriodType.trim().toUpperCase();
      if (name && !customPeriodTypes.includes(name)) {
        setCustomPeriodTypes([...customPeriodTypes, name]);
        setNewPeriodType('');
      }
    };

    // Remove period type
    const removePeriodType = (name: string) => {
      setCustomPeriodTypes(customPeriodTypes.filter(pt => pt !== name));
    };

    // Expose save and canSave to parent via ref
    useImperativeHandle(ref, () => ({
      canSave: () => {
        return !!(scheduleType && customDayTypes.length > 0 && customPeriodTypes.length > 0 && lunchType);
      },
      save: async () => {
        if (!scheduleType || customDayTypes.length === 0 || customPeriodTypes.length === 0 || !lunchType) {
          toast.error('Please complete all sections');
          return false;
        }

        try {
          const promises = [];

          // 1. Save schedule type
          if (scheduleType !== school.scheduleType) {
            promises.push(updateScheduleTypeMutation.mutateAsync(scheduleType));
          }

          // 2. Sync day types
          const existingDayTypeNames = new Set(school.dayTypes?.map(dt => dt.name) || []);
          const wantedDayTypeNames = new Set(customDayTypes);
          
          // Delete removed ones
          for (const dt of school.dayTypes || []) {
            if (!wantedDayTypeNames.has(dt.name)) {
              await deleteDayTypeMutation.mutateAsync(dt.id);
            }
          }
          
          // Create new ones
          for (const name of customDayTypes) {
            if (!existingDayTypeNames.has(name)) {
              promises.push(createDayTypeMutation.mutateAsync(name.replace(/[^A-Z0-9/]/g, '')));
            }
          }

          // 3. Sync period types
          const existingPeriodTypeNames = new Set(school.periodTypes?.map(pt => pt.name) || []);
          const wantedPeriodTypeNames = new Set(customPeriodTypes);
          
          // Delete removed ones
          for (const pt of school.periodTypes || []) {
            if (!wantedPeriodTypeNames.has(pt.name)) {
              await deletePeriodTypeMutation.mutateAsync(pt.id);
            }
          }
          
          // Create new ones
          for (const name of customPeriodTypes) {
            if (!existingPeriodTypeNames.has(name)) {
              promises.push(createPeriodTypeMutation.mutateAsync(name.replace(/[^A-Z0-9/]/g, '')));
            }
          }

          // 4. Handle lunch settings
          if (lunchType !== school.lunchSettings?.type) {
            switch (lunchType) {
              case 'unit':
                if (lunchClassId) {
                  promises.push(deleteLunchClassMutation.mutateAsync(lunchClassId));
                }
                if (school.lunchSettings?.type === 'waves') {
                  promises.push(updateLunchSlotsMutation.mutateAsync(0));
                }
                break;
              case 'class':
                if (school.lunchSettings?.type === 'waves') {
                  promises.push(updateLunchSlotsMutation.mutateAsync(0));
                }
                promises.push(createLunchClassMutation.mutateAsync());
                break;
              case 'waves':
                if (lunchClassId) {
                  promises.push(deleteLunchClassMutation.mutateAsync(lunchClassId));
                }
                promises.push(updateLunchSlotsMutation.mutateAsync(numLunchWaves));
                break;
            }
          } else if (lunchType === 'waves' && school.lunchSettings?.type === 'waves' && numLunchWaves !== school.lunchSettings.slots.length) {
            promises.push(updateLunchSlotsMutation.mutateAsync(numLunchWaves));
          }

          await Promise.all(promises);
          toast.success('Schedule configuration saved');
          return true;
        } catch (error) {
          console.error('Step 1 save error:', error);
          toast.error('Failed to save configuration. Please try again.');
          return false;
        }
      }
    }));

    return (
      <div className="space-y-8">
        {/* Schedule Type Selection */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Schedule Pattern</h3>
            <p className="text-sm text-muted-foreground mt-1">How does your schedule rotate?</p>
          </div>
          
          <Select value={scheduleType} onValueChange={handleScheduleTypeChange}>
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Choose schedule type" />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULE_TYPES.map((st) => (
                <SelectItem key={st.value} value={st.value}>
                  {st.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Day Types */}
        {scheduleType && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Day Types</Label>
              {customDayTypes.length === 1 && (scheduleType === 'same_every_day' || scheduleType === 'same_every_day_plus') && (
                <span className="text-xs text-muted-foreground">Single day type is valid for this schedule</span>
              )}
            </div>
            
            <div 
              className="flex flex-wrap gap-2 p-4 rounded-xl border-2 min-h-[52px]"
              style={{ borderColor: `${schoolPrimaryColor}20` }}
            >
              {customDayTypes.map((name) => (
                <Badge
                  key={name}
                  variant="secondary"
                  className="h-8 px-3 text-sm font-medium gap-1"
                  style={{ 
                    backgroundColor: `${schoolPrimaryColor}15`,
                    color: schoolPrimaryColor,
                  }}
                >
                  {name}
                  <button
                    onClick={() => removeDayType(name)}
                    className="ml-1 hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              
              <div className="flex items-center gap-1">
                <Input
                  value={newDayType}
                  onChange={(e) => setNewDayType(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomDayType();
                    }
                  }}
                  placeholder="Add..."
                  className="h-8 w-20 text-sm"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addCustomDayType}
                  className="h-8 w-8 p-0"
                  disabled={!newDayType.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Period Types */}
        {scheduleType && (
          <div className="space-y-3">
            <Label className="text-base font-semibold">Period Types</Label>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {PERIOD_PRESETS.map((preset) => {
                const isMatch = JSON.stringify(preset.value) === JSON.stringify(customPeriodTypes);
                return (
                  <Button
                    key={preset.label}
                    variant={isMatch ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePeriodPresetSelect(preset.value)}
                    style={isMatch ? { 
                      backgroundColor: schoolPrimaryColor,
                      color: getContrastColor(schoolPrimaryColor)
                    } : {}}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </div>
            
            <div 
              className="flex flex-wrap gap-2 p-4 rounded-xl border-2 min-h-[52px]"
              style={{ borderColor: `${schoolPrimaryColor}20` }}
            >
              {customPeriodTypes.map((name) => (
                <Badge
                  key={name}
                  variant="secondary"
                  className="h-8 px-3 text-sm font-medium gap-1"
                  style={{ 
                    backgroundColor: `${schoolPrimaryColor}15`,
                    color: schoolPrimaryColor,
                  }}
                >
                  {name}
                  <button
                    onClick={() => removePeriodType(name)}
                    className="ml-1 hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              
              <div className="flex items-center gap-1">
                <Input
                  value={newPeriodType}
                  onChange={(e) => setNewPeriodType(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomPeriodType();
                    }
                  }}
                  placeholder="Add..."
                  className="h-8 w-20 text-sm"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addCustomPeriodType}
                  className="h-8 w-8 p-0"
                  disabled={!newPeriodType.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Lunch Type */}
        {scheduleType && (
          <div className="space-y-3">
            <Label className="text-base font-semibold">Lunch Settings</Label>
            
            <RadioGroup
              value={lunchType}
              onValueChange={(value) => setLunchType(value as 'unit' | 'class' | 'waves')}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3"
            >
              {LUNCH_TYPES.map((lt) => {
                const isSelected = lunchType === lt.value;
                return (
                  <label
                    key={lt.value}
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-sm",
                      isSelected && "ring-2 ring-offset-2"
                    )}
                    style={isSelected ? { 
                      borderColor: schoolPrimaryColor, 
                      backgroundColor: `${schoolPrimaryColor}08`,
                      ['--tw-ring-color' as string]: schoolPrimaryColor
                    } : {}}
                  >
                    <RadioGroupItem value={lt.value} id={`lunch-${lt.value}`} />
                    <span className="font-medium">{lt.label}</span>
                  </label>
                );
              })}
            </RadioGroup>

            {lunchType === 'waves' && (
              <div 
                className="flex items-center gap-3 p-4 rounded-xl"
                style={{ backgroundColor: `${schoolPrimaryColor}08` }}
              >
                <Label htmlFor="numWaves" className="font-medium whitespace-nowrap">Number of waves:</Label>
                <Input
                  id="numWaves"
                  type="number"
                  min="1"
                  max="10"
                  value={numLunchWaves}
                  onChange={(e) => setNumLunchWaves(parseInt(e.target.value) || 1)}
                  className="w-20 h-9"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
